/**
 * Session Suspend Modal
 * Modal for suspending an active cash register session
 */

import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useResumeSession, useSuspendSession } from '@/hooks/pos';
import type { CashRegisterSession } from '@/types/pos.types';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';

interface SessionSuspendModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session?: CashRegisterSession;
  onSessionUpdated: () => void;
}

export function SessionSuspendModal({
  open,
  onOpenChange,
  session,
  onSessionUpdated,
}: SessionSuspendModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<string>('');
  const suspendSession = useSuspendSession();
  const resumeSession = useResumeSession();

  const isSuspended = session?.status === 'SUSPENDED';

  const handleSuspend = async () => {
    if (!session) return;
    try {
      await suspendSession.mutateAsync({
        sessionId: session.id,
        dto: reason ? { reason } : undefined,
      });
      onSessionUpdated();
      setReason('');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to suspend session:', error);
    }
  };

  const handleResume = async () => {
    if (!session) return;
    try {
      await resumeSession.mutateAsync(session.id);
      onSessionUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to resume session:', error);
    }
  };

  const isPending = suspendSession.isPending || resumeSession.isPending;

  // Common suspend reasons
  const quickReasons = useMemo(
    () => [
      { key: 'lunch', label: t('sales.session.suspend.quickReasons.lunch') },
      { key: 'break', label: t('sales.session.suspend.quickReasons.break') },
      { key: 'shiftChange', label: t('sales.session.suspend.quickReasons.shiftChange') },
      { key: 'technical', label: t('sales.session.suspend.quickReasons.technical') },
    ],
    [t]
  );

  if (!open || !session) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {isSuspended
              ? t('sales.session.suspend.resumeTitle')
              : t('sales.session.suspend.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Session info */}
          <div className="rounded-lg bg-gray-50 dark:bg-[#1f5f6b] p-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-[#ccfbf1]">
                  {t('sales.session.close.sessionLabel')}
                </span>
                <span className="font-medium">{session.sessionNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-[#ccfbf1]">
                  {t('sales.session.suspend.statusLabel')}
                </span>
                <span
                  className={`font-medium ${isSuspended ? 'text-yellow-600' : 'text-green-600'}`}
                >
                  {isSuspended
                    ? t('sales.session.suspend.statusSuspended')
                    : t('sales.session.suspend.statusActive')}
                </span>
              </div>
            </div>
          </div>

          {/* Suspend form */}
          {!isSuspended && (
            <>
              <div>
                <label htmlFor="pos-suspend-reason" className="mb-2 block text-sm font-medium">
                  {t('sales.session.suspend.reasonLabel')}
                </label>
                <Input
                  id="pos-suspend-reason"
                  type="text"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder={t('sales.session.suspend.reasonPlaceholder')}
                />
              </div>

              {/* Quick reason buttons */}
              <div className="flex flex-wrap gap-2">
                {quickReasons.map(r => (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setReason(r.label)}
                    className={`rounded px-3 py-1 text-sm ${
                      reason === r.label
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-100 dark:bg-[#1f5f6b] hover:bg-gray-200 dark:hover:bg-[#0f3d47]'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Resume info */}
          {isSuspended && (
            <div className="rounded-lg bg-yellow-50 p-4 text-sm text-yellow-700">
              <p className="font-medium">{t('sales.session.suspend.resumeInfo')}</p>
              <p className="mt-1">{t('sales.session.suspend.resumeHint')}</p>
            </div>
          )}

          {/* Error display */}
          {(suspendSession.isError || resumeSession.isError) && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-600 dark:text-red-400">
              {(suspendSession.error || resumeSession.error) instanceof Error
                ? (suspendSession.error || resumeSession.error)?.message
                : t('toast.error')}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={isPending}
            >
              {t('common.cancel')}
            </Button>
            {isSuspended ? (
              <Button
                type="button"
                onClick={handleResume}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={isPending}
              >
                {resumeSession.isPending
                  ? t('sales.session.suspend.resuming')
                  : t('sales.session.suspend.resumeButton')}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSuspend}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600"
                disabled={isPending}
              >
                {suspendSession.isPending
                  ? t('sales.session.suspend.suspending')
                  : t('sales.session.suspend.suspendButton')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
